/* eslint-disable indent */
/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { FaEdit, FaTrash, FaSave } from "react-icons/fa";
import { useAuthContext } from "../../context/auth";
import { client } from "../../utils/supabase";
import "./stylesCarteleriaList.scss";

const CarteleriaList = ({ onEdit: originalOnEdit, refreshKey }) => {
  const [negocios, setNegocios] = useState([]);
  const [clientes, setClientes] = useState({});
  const [edificios, setEdificios] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [usuarios, setUsuarios] = useState({});

  const { pais } = useAuthContext();
  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : null;

  const localString = useCallback(
    () => ({ Chile: "es-CL", Perú: "es-PE", Uruguay: "es-UY" })[normalizedPais] || "es-UY",
    [normalizedPais]
  );

  const getCurrencyDisplay = (moneda) => {
    switch (moneda) {
      case "UF":
        return "UF ";
      case "USD":
        return "US$ ";
      case "CLP":
        return "$ ";
      case "PEN":
        return "S/ ";
      default:
        return "$ ";
    }
  };

  const getDecimals = (moneda) => (moneda === "UF" ? 2 : { Chile: 0, Perú: 2, Uruguay: 0 }[normalizedPais] || 0);

  const UF_VALUE_CLP = 39000;
  const USD_TO_CLP = 950;

  const convertToLocalCurrency = (amount, monedaOrigen, monedaDestino) => {
    if (!amount) return 0;
    let inCLP = parseFloat(amount);

    // Convertir a CLP si es necesario
    if (monedaOrigen === "USD") {
      inCLP = inCLP * USD_TO_CLP;
    } else if (monedaOrigen === "UF") {
      inCLP = inCLP * UF_VALUE_CLP;
    }
    // Si ya está en CLP, no hacer nada

    // Convertir a moneda destino
    if (monedaDestino === "UF") {
      return inCLP / UF_VALUE_CLP;
    } else if (monedaDestino === "PEN") {
      // Asumimos que si no es Chile, es Perú y ya está en PEN
      return monedaOrigen === "PEN" ? inCLP : inCLP / 3900; // Ajuste aproximado si necesitas CLP → PEN
    }
    return inCLP;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [clientesResponse, usuariosResponse, edificiosResponse, negociosResponse] = await Promise.all([
        client.from("clientes").select("id, nombre").eq("pais", normalizedPais).order("nombre", { ascending: true }),
        client.from("usuarios").select("id, nombre").order("nombre", { ascending: true }),
        client
          .from("edificios")
          .select("id, edificio")
          .eq("pais", normalizedPais)
          .order("edificio", { ascending: true }),
        client
          .from("negocios_carteleria")
          .select("*")
          .eq("pais", normalizedPais)
          .eq("eliminada", false)
          .order("id", { ascending: false }),
      ]);

      if (clientesResponse.error) throw new Error(`Clientes: ${clientesResponse.error.message}`);
      if (edificiosResponse.error) throw new Error(`Edificios: ${edificiosResponse.error.message}`);
      if (negociosResponse.error) throw new Error(`Negocios: ${negociosResponse.error.message}`);

      setClientes(clientesResponse.data.reduce((acc, c) => ({ ...acc, [c.id]: c.nombre }), {}));
      setEdificios(edificiosResponse.data.reduce((acc, e) => ({ ...acc, [e.id]: e.edificio }), {}));
      setNegocios(negociosResponse.data || []);
      setUsuarios(usuariosResponse.data.reduce((acc, u) => ({ ...acc, [u.id]: u.nombre }), {}));
    } catch (err) {
      console.error("Error fetching data:", err.message);
      setError(err.message);
      toast.error("Error al cargar los datos: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!normalizedPais) {
      setError("No se ha definido el país del usuario");
      setLoading(false);
      return;
    }
    fetchData();
  }, [normalizedPais, refreshKey]);

  const handleEditStatus = async (id, newStatus) => {
    try {
      const { error } = await client.from("negocios_carteleria").update({ estado: newStatus }).eq("id", id);
      if (error) throw error;
      toast.success("Estado actualizado exitosamente");
      setEditId(null);
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error al actualizar el estado: " + error.message);
    }
  };

  const deleteNegocio = (id) => {
    if (!deleteId) {
      setDeleteId(id);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const negocio = negocios.find((n) => n.id === deleteId);
    if (negocio.estado.toLowerCase() === "activo") {
      toast.error("No se puede eliminar un proyecto ACTIVO.");
      setDeleteId(null);
      return;
    }
    try {
      const { error } = await client.from("negocios_carteleria").update({ eliminada: true }).eq("id", deleteId);
      if (error) throw error;
      toast.success("Oportunidad marcada como eliminada");
      fetchData();
    } catch (error) {
      console.error("Error marking as deleted:", error);
      toast.error("Error al marcar como eliminada: " + error.message);
    } finally {
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteId(null);
  };

  const calculateEndDate = (fechaInicio, plazoMeses) => {
    const startDate = new Date(fechaInicio);
    const endDate = new Date(startDate);
    endDate.setMonth(startDate.getMonth() + parseInt(plazoMeses || 1));

    const today = new Date();
    const diffTime = endDate - today;
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30)); // redondea hacia arriba

    if (diffTime < 0) {
      return { text: "Vencido", isExpired: true, isNearEnd: false };
    } else if (diffMonths <= 6) {
      return { text: `${diffMonths} meses`, isExpired: false, isNearEnd: true };
    } else {
      return { text: "> 6 meses", isExpired: false, isNearEnd: false };
    }
  };

  const sortedNegocios = [...negocios].sort((a, b) => {
    // 1. Orden por estado
    const stateOrder = {
      activo: 6,
      "100%": 5,
      "90%": 4,
      "75%": 3,
      "50%": 2,
      "0%": 1,
    };

    const estadoA = (a.estado || "").toString().toLowerCase();
    const estadoB = (b.estado || "").toString().toLowerCase();
    const stateA = stateOrder[estadoA] || 0;
    const stateB = stateOrder[estadoB] || 0;

    if (stateA !== stateB) return stateB - stateA;

    // 2. Orden por fecha_inicio (más antiguos primero)
    const dateA = new Date(a.fecha_inicio).getTime();
    const dateB = new Date(b.fecha_inicio).getTime();
    if (dateA !== dateB) return dateA - dateB;

    // 3. Orden alfabético por nombre
    const nombreA =
      a.tipo_vertical === "TD"
        ? clientes[a.referencia_id] || `ID ${a.referencia_id}`
        : edificios[a.referencia_id] || `ID ${a.referencia_id}`;
    const nombreB =
      b.tipo_vertical === "TD"
        ? clientes[b.referencia_id] || `ID ${b.referencia_id}`
        : edificios[b.referencia_id] || `ID ${b.referencia_id}`;

    return nombreA.localeCompare(nombreB);
  });

  if (error) {
    return <div className="carteleria-list">{error}</div>;
  }
  if (loading) {
    return <div className="carteleria-list">Cargando oportunidades...</div>;
  }

  const getStatusClass = (estado) => {
    switch (estado.toLowerCase()) {
      case "activo":
        return "bg-custom-violet text-white";
      case "100%":
        return "bg-success text-white";
      case "90%":
        return "bg-danger text-white";
      case "75%":
        return "bg-primary text-white";
      case "50%":
        return "bg-warning text-dark";
      case "0%":
        return "bg-dark text-white";
      default:
        return "";
    }
  };

  return (
    <div className="carteleria-list" style={{ overflowX: "auto", width: "100%", maxWidth: "100%", display: "block" }}>
      {sortedNegocios.length === 0 ? (
        <div>No se encontraron oportunidades para {normalizedPais}</div>
      ) : (
        <>
          <table className="carteleria-table">
            <thead>
              <tr>
                <th rowSpan="2">Tipo</th>
                <th rowSpan="2">Ejecutivo</th>
                <th rowSpan="2">Cliente/Edificio</th>
                <th rowSpan="2">Status</th>
                <th rowSpan="2">Pantallas</th>
                <th colSpan="2">Contrato</th>
                <th>Hardware</th>
                <th colSpan="4">Fee Mensual</th>
                <th rowSpan="2">Edit</th>
              </tr>
              <tr>
                <th>Inicio</th>
                <th>Fin</th>
                <th>CAPEX</th>
                <th>OPEX</th>
                <th>Gestión</th>
                <th>Contenidos</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {sortedNegocios.map((n) => {
                const gestionDisplay = getCurrencyDisplay(n.moneda_gestion);
                const gestionDecimals = getDecimals(n.moneda_gestion);
                const locale = localString();
                const isActive = n.estado.toLowerCase() === "activo";

                const monedaObjetivo = normalizedPais === "Chile" ? "UF" : "PEN";

                const feeGestionUF = convertToLocalCurrency(n.fee_gestion_unitario, n.moneda_gestion, monedaObjetivo);
                const feeContenidosUF = n.incluye_contenidos
                  ? convertToLocalCurrency(n.fee_contenidos_unitario, n.moneda_contenidos, monedaObjetivo)
                  : 0;
                const cuotaOpexUF =
                  n.incluye_hardware && n.modalidad === "OPEX"
                    ? convertToLocalCurrency(n.cuota_hardware_opex, n.moneda_hardware, monedaObjetivo)
                    : 0;

                const totalUnitario = feeGestionUF + feeContenidosUF + cuotaOpexUF;
                const totalMensual = totalUnitario * (n.cantidad_pantallas || 1);

                return (
                  <tr key={n.id}>
                    <td>{n.tipo_vertical}</td>
                    <td>{usuarios[n.ejecutivo] || "-"}</td>
                    <td>
                      {n.tipo_vertical === "TD"
                        ? (clientes[n.referencia_id] || `ID ${n.referencia_id}`).toUpperCase()
                        : (edificios[n.referencia_id] || `ID ${n.referencia_id}`).toUpperCase()}
                    </td>
                    <td className={`uppercase ${isActive ? "active" : ""} ${getStatusClass(n.estado)}`}>
                      {editId === n.id ? (
                        <select
                          value={n.estado}
                          onChange={(e) => handleEditStatus(n.id, e.target.value)}
                          onBlur={() => setEditId(null)}
                          autoFocus
                          style={{ padding: "1px", width: "100%", fontSize: "0.7rem" }}
                        >
                          <option value="Activo">Activo</option>
                          <option value="100%">100%</option>
                          <option value="90%">90%</option>
                          <option value="75%">75%</option>
                          <option value="50%">50%</option>
                          <option value="0%">0%</option>
                        </select>
                      ) : n.estado === "100%" ? (
                        "100%"
                      ) : n.estado === "Activo" ? (
                        "ACTIVO"
                      ) : (
                        n.estado.toUpperCase()
                      )}
                    </td>
                    <td>{n.cantidad_pantallas || "?"}</td>
                    <td>
                      {n.estado.toLowerCase() === "activo"
                        ? (() => {
                            const date = new Date(n.fecha_inicio);
                            const month = String(date.getMonth() + 1).padStart(2, "0"); // 1 → "01"
                            const year = date.getFullYear();
                            return `${month}/${year}`;
                          })()
                        : "-"}
                    </td>
                    <td>
                      {n.estado.toLowerCase() === "activo"
                        ? (() => {
                            const result = calculateEndDate(n.fecha_inicio, n.plazo_contrato_meses);
                            return (
                              <span
                                style={{
                                  fontWeight: result.isExpired ? "bold" : "normal",
                                  color: result.isExpired || result.isNearEnd ? "#dc3545" : "inherit",
                                }}
                              >
                                {result.text}
                              </span>
                            );
                          })()
                        : "-"}
                    </td>
                    <td>
                      {n.incluye_hardware && n.modalidad === "CAPEX" ? (
                        <>
                          {getCurrencyDisplay(n.moneda_hardware)}
                          {(n.monto_hardware || 0).toLocaleString(locale, {
                            minimumFractionDigits: getDecimals(n.moneda_hardware),
                            maximumFractionDigits: getDecimals(n.moneda_hardware),
                          })}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {n.incluye_hardware && n.modalidad === "OPEX" ? (
                        <>
                          {getCurrencyDisplay(n.moneda_hardware)}
                          {(n.cuota_hardware_opex || 0).toLocaleString(locale, {
                            minimumFractionDigits: getDecimals(n.moneda_hardware),
                            maximumFractionDigits: getDecimals(n.moneda_hardware),
                          })}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>

                    {/* GESTIÓN */}
                    <td>
                      {gestionDisplay}
                      {(n.fee_gestion_unitario || 0).toLocaleString(locale, {
                        minimumFractionDigits: gestionDecimals,
                        maximumFractionDigits: gestionDecimals,
                      })}
                    </td>

                    {/* CONTENIDOS */}
                    <td>
                      {n.incluye_contenidos
                        ? `${getCurrencyDisplay(n.moneda_contenidos)}${(n.fee_contenidos_unitario || 0).toLocaleString(
                            locale,
                            {
                              minimumFractionDigits: getDecimals(n.moneda_contenidos),
                              maximumFractionDigits: getDecimals(n.moneda_contenidos),
                            }
                          )}`
                        : "-"}
                    </td>

                    {/* TOTAL MENSUAL */}
                    <td className="center" style={{ fontWeight: "600" }}>
                      {monedaObjetivo === "UF" ? "UF " : "S/ "}
                      {totalMensual.toLocaleString(locale, {
                        minimumFractionDigits: monedaObjetivo === "UF" ? 2 : 2,
                        maximumFractionDigits: monedaObjetivo === "UF" ? 2 : 2,
                      })}
                    </td>
                    <td className="center">
                      {editId === n.id ? (
                        <button onClick={() => setEditId(null)} className="action-button-carteleria edit-button">
                          <FaSave />
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => !isActive && setEditId(n.id)}
                            className="action-button-carteleria edit-button"
                            disabled={isActive}
                          >
                            <FaEdit />
                          </button>
                          <span>|</span>
                          <button
                            onClick={() => !isActive && deleteNegocio(n.id)}
                            className="action-button-carteleria danger"
                            disabled={isActive}
                          >
                            <FaTrash />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {deleteId && (
            <div
              className="modal"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ backgroundColor: "white", padding: "10px", borderRadius: "5px", textAlign: "center" }}>
                <p>¿Está seguro de eliminar esta oportunidad?</p>
                <button
                  onClick={confirmDelete}
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "2px 1px",
                    cursor: "pointer",
                  }}
                >
                  Sí
                </button>
                <button
                  onClick={cancelDelete}
                  style={{
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "2px 1px",
                    cursor: "pointer",
                  }}
                >
                  No
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CarteleriaList;
